import { Button, InputNumber } from 'antd';
import { CheckOutlined, ExclamationCircleOutlined, ScanOutlined } from '@ant-design/icons';
import type { ScanJiraDaysState } from '@/apis/simulation';
import styles from './GenerateCards.module.less';
import { Hint, StatusLine, Stepper } from './generateCardParts';

const DAY_PRESETS = [1, 3, 7, 30];
const PROJECTS = ['AIP', 'DC', 'GC'];

interface Props {
  scanDays: number;
  onScanDaysChange: (v: number) => void;
  projects: string[];
  onProjectsChange: (v: string[]) => void;
  state: ScanJiraDaysState | null;
  stateLoading: boolean;
  onRefresh: () => void;
  submitting: boolean;
  onSubmit: () => void;
}

/** 自动扫描卡：按 Jira「创建时间」扫最近 N 天、所选项目的 Issue，批量建场景并统一调度构建。 */
const ScanJiraCard: React.FC<Props> = props => {
  const { scanDays, projects, state } = props;
  const status = state?.status ?? 'idle';
  const running = status === 'running';

  const toggleProject = (p: string) => {
    const next = projects.includes(p) ? projects.filter(x => x !== p) : [...projects, p];
    // 保持 AIP / DC / GC 固定顺序，摘要与请求参数稳定
    props.onProjectsChange(PROJECTS.filter(x => next.includes(x)));
  };

  // 扫描已是全局单实例锁：running 时其他用户也会看到，必须告知是谁起的
  const meta: string[] = [];
  if (state?.scanDays) meta.push(`${state.scanDays} 天`);
  if (state?.projects?.length) meta.push(state.projects.join('/'));
  if (state?.triggeredBy) meta.push(`由 ${state.triggeredBy} 触发`);
  if (running && state?.scannedCount != null) {
    meta.push(state.totalCount != null
      ? `已扫描 ${state.scannedCount} / ${state.totalCount}`
      : `已扫描 ${state.scannedCount}`);
  }
  if (status === 'completed' && state?.result) {
    const r = state.result;
    meta.push(`扫描 ${r.jiraIssueCount ?? 0} 个 Issue`);
    if (r.success) meta.push(`${r.success} 新建`);
    if (r.existed) meta.push(`${r.existed} 已存在`);
    if (r.skipped) meta.push(`${r.skipped} 跳过`);
    if (r.failed) meta.push(`${r.failed} 失败`);
  }
  const jobId = status === 'completed' ? state?.result?.batchBuild?.jobId : undefined;
  const pct = running && state?.scannedCount != null && state?.totalCount
    ? Math.min(100, Math.round((state.scannedCount / state.totalCount) * 100))
    : null;

  return (
    <section className={styles.card} aria-label="自动扫描">
      <header className={styles.header}>
        <div>
          <div className={styles.title}>
            <span className={styles.titleIcon}><ScanOutlined /></span>
            自动扫描
          </div>
          <div className={styles.subtitle}>扫描最近 N 天新建的 Jira Issue，批量建场景并统一调度构建</div>
        </div>
        <Stepper steps={['扫描 Jira', '创建场景', '调度构建']} status={status} activeIndex={0} />
      </header>

      <div className={styles.body}>
        <div className={styles.section}>
          <div className={styles.sectionHead}>
            <span className={styles.sectionNo}>01</span>
            <span className={styles.sectionTitle}>时间窗口</span>
            <Hint title="按 Jira「创建时间」筛选最近 N 天内新创建的 Issue（老 case 补打 logsim 标签请走「场景批量生成」的 Jira 入口）" />
          </div>
          <div className={styles.daysRow}>
            <div className={styles.pills} role="radiogroup" aria-label="常用天数">
              {DAY_PRESETS.map(d => (
                <button
                  key={d}
                  type="button"
                  role="radio"
                  aria-checked={scanDays === d}
                  className={`${styles.pill} ${scanDays === d ? styles.pillOn : ''}`}
                  onClick={() => props.onScanDaysChange(d)}
                >
                  {d} 天
                </button>
              ))}
            </div>
            <span className={styles.unit}>或自定义</span>
            <InputNumber
              className={styles.daysInput}
              aria-label="扫描天数"
              min={1}
              max={365}
              value={scanDays}
              onChange={v => props.onScanDaysChange(typeof v === 'number' ? v : 7)}
              suffix="天"
            />
          </div>
        </div>

        <div className={styles.section}>
          <div className={styles.sectionHead}>
            <span className={styles.sectionNo}>02</span>
            <span className={styles.sectionTitle}>Jira 项目</span>
            <span className={styles.sectionHint}>多选，至少一个</span>
          </div>
          <div className={styles.chips} role="group" aria-label="Jira 项目">
            {PROJECTS.map(p => {
              const on = projects.includes(p);
              return (
                <button
                  key={p}
                  type="button"
                  aria-pressed={on}
                  className={`${styles.chip} ${on ? styles.chipOn : ''}`}
                  onClick={() => toggleProject(p)}
                >
                  <span className={styles.chipBox}><CheckOutlined /></span>
                  {p}
                </button>
              );
            })}
          </div>
        </div>

        <div className={styles.section}>
          <div className={styles.sectionHead}>
            <span className={styles.sectionNo}>03</span>
            <span className={styles.sectionTitle}>扫描条件预览</span>
          </div>
          <div className={styles.query} aria-label="扫描条件">
            <span className={styles.kw}>project</span> <span className={styles.muted}>in</span>{' '}
            (<span className={styles.val}>{projects.length ? projects.join(', ') : '—'}</span>){' '}
            <span className={styles.kw}>AND</span> <span className={styles.kw}>created</span>{' '}
            <span className={styles.muted}>&gt;=</span> <span className={styles.val}>-{scanDays}d</span>
          </div>
          {/* 单次扫描上限：与后端 jira_service.iter_issues_paged total_cap=500_000 对齐。
              修改阈值时**两边必须同步**（后端 default + 这段文案），同时改测试
              tests/test_jira_paged_scan.py::TestDefaultTotalCap._EXPECTED_DEFAULT。
              多项目扫描时 50 万是所选项目**合计**上限（DC 量大时会挤占 AIP 名额）。 */}
          <div className={styles.caveat}>
            <ExclamationCircleOutlined style={{ marginTop: 3 }} />
            <span>单次扫描最多处理所选项目合计 <b>50 万</b> 条 Issue，超出部分按创建时间倒序截断（保留最新）</span>
          </div>
        </div>
      </div>

      <footer className={styles.footer}>
        <StatusLine
          status={status}
          runningText="扫描中"
          meta={meta}
          jobId={jobId}
          error={state?.error}
          loading={props.stateLoading}
          onRefresh={props.onRefresh}
        >
          {running && (
            <div className={styles.progress} aria-hidden>
              <div
                className={`${styles.progressBar} ${pct == null ? styles.progressIndeterminate : ''}`}
                style={pct == null ? undefined : { width: `${pct}%` }}
              />
            </div>
          )}
        </StatusLine>
        <Button
          className={styles.submit}
          type="primary"
          icon={<ScanOutlined />}
          loading={props.submitting}
          onClick={props.onSubmit}
          disabled={running || projects.length === 0}
        >
          {running ? '扫描中…' : '开始扫描'}
        </Button>
      </footer>
    </section>
  );
};

export default ScanJiraCard;
